import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { FetchingState, InvoiceNumberObject } from '@payer-admin-ui/components-v2/src/types/state';
import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import { DetailedInvoicePayment, VatEntry } from '@payer-admin-ui/api/src/invoices/responses/InternalInvoiceResponse';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { InvoiceType } from '@payer-admin-ui/api/src/invoices/models/InvoiceType';
import { InvoiceStatus } from '@payer-admin-ui/api/src/invoices/models/InvoiceStatus';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { PaymentMethod } from '@payer-admin-ui/api/src/invoices/models/PaymentMethod';
import { BankAccount, InvoiceItemV2 } from '@payer-admin-ui/api/src/invoices/models/ResponseInvoiceV2';
import { PaymentStatus } from '@payer-admin-ui/api/src/invoices/models/PaymentStatus';
import { OrderCustomer } from '@payer-admin-ui/api/src/invoices/models/OrderCustomer';
import { ExternalOrderStatus } from '@payer-admin-ui/api/src/invoices/models/ExternalOrderStatus';
import { InvoicePeriod } from '@payer-admin-ui/api/src/invoices/models/InvoicePeriod';
import { ZonedDateTime } from '@payer-admin-ui/api/src/external/java/ZonedDateTime';
import { ResponseInvoiceReminder } from '@payer-admin-ui/api/src/invoices/responses/ResponseInvoiceReminder';
import { NamedReference } from '@payer-admin-ui/api/src/invoices/models/NamedReference';
import { DebtCollectionConfig } from '@payer-admin-ui/api/src/invoices/models/DebtCollectionConfig';
import { InternalInvoiceHistoryEntryResponse } from '@payer-admin-ui/api/src/invoices/history/models/InternalInvoiceHistoryEntryResponse';
import {
  InternalInvoiceResponseV3,
  ResponseInvoiceV3,
} from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { MerchantAdminDispatch } from '../../../../redux/store';

export const updateInvoice = createAsyncThunk<
  ResponseInvoiceV3,
  { invoiceEntity: UpdateInvoiceData; invoiceNumber: number },
  { dispatch: MerchantAdminDispatch }
>('invoices/actions/UpdateInvoiceModal/update', async ({ invoiceEntity, invoiceNumber }) => {
  return await invoiceAPI.actions.updateInvoice(
    {
      deliveryType: invoiceEntity.deliveryType,
      dueDate: invoiceEntity.dueDate,
      orderReference: invoiceEntity.orderReference,
      yourReference: invoiceEntity.yourReference,
      ourReference: invoiceEntity.ourReference,
      yourOrderNumber: invoiceEntity.yourOrderNumber,
      site: invoiceEntity.site,
      comment: invoiceEntity.comment,
      scheduledReminders: undefined,
      debtCollectionDate: undefined,
      paymentMethods: invoiceEntity.paymentMethods,
      languageCode: invoiceEntity.invoiceCustomer.address.languageCode,
      customer: {
        companyName: invoiceEntity.invoiceCustomer.address.companyName,
        yourReference: invoiceEntity.invoiceCustomer.address.yourReference,
        firstName: invoiceEntity.invoiceCustomer.address.firstName,
        lastName: invoiceEntity.invoiceCustomer.address.lastName,
        streetAddress1: invoiceEntity.invoiceCustomer.address.streetAddress1,
        streetAddress2: invoiceEntity.invoiceCustomer.address.streetAddress2,
        zipCode: invoiceEntity.invoiceCustomer.address.zipCode,
        city: invoiceEntity.invoiceCustomer.address.city,
        phoneNumber: invoiceEntity.invoiceCustomer.address.phoneNumber,
        emailAddress: invoiceEntity.invoiceCustomer.address.emailAddress,
        emailAddressCc: invoiceEntity.invoiceCustomer.address.emailAddressCc,
        emailAddressBcc: invoiceEntity.invoiceCustomer.address.emailAddressBcc,
        customerType: invoiceEntity.invoiceCustomer.customerType,
        externalCustomerNumber: invoiceEntity.invoiceCustomer.customerNumber,
        regNumber: invoiceEntity.invoiceCustomer.regNumber,
        customerVatId: invoiceEntity.invoiceCustomer.customerVatId,
        gln: invoiceEntity.invoiceCustomer.address.gln,
      },
    },
    invoiceNumber,
  );
});

export const fetchInvoiceByInvoiceNumber = createAsyncThunk<InternalInvoiceResponseV3 | null, InvoiceNumberObject>(
  'invoices/actions/UpdateInvoiceModal/fetchInvoiceByInvoiceNumber',
  async ({ invoiceNumber }) => {
    return invoiceAPI.fetchByInvoiceNumber({ invoiceNumber });
  },
);

export type UpdateInvoiceData = {
  id?: number;
  orderId?: number;
  orderReference: string;
  additionalReferenceId?: string;
  description?: string | null;
  comment?: string;
  yourReference?: string;
  yourOrderNumber?: string;
  invoiceNumber: number;
  externalInvoiceNumber?: string;
  uniqueId: string; // UUID
  parentInvoiceNumber?: number;
  websiteInvoiceSeqNo?: number;
  referenceNumber?: string; // OCR-number
  invoiceDate?: LocalDate;
  dueDate?: LocalDate;
  isOverdue: boolean;
  amountToPay: number;
  currencyCode: CurrencyCode;
  countryCode: CountryCode;
  type: InvoiceType;
  status: InvoiceStatus;
  deliveryType: DeliveryType;
  paymentMethods: PaymentMethod[];
  items: InvoiceItemV2[];
  creditedAmount?: number;
  paymentStatus: PaymentStatus;
  payments: DetailedInvoicePayment[];
  balance: number;
  paymentBankAccounts: BankAccount[];
  publicInvoiceUrl: string;
  invoicePdfUrl: string;
  invoiceCustomer: OrderCustomer;
  sellingCustomer?: OrderCustomer;
  orderStatus: ExternalOrderStatus;
  ourReference?: string;
  site?: string;
  project?: string;
  invoicePeriod: InvoicePeriod;
  orderApproveDate?: ZonedDateTime;
  creditsInvoiceWithInvoiceNumber?: number;
  reminders: ResponseInvoiceReminder[];
  remindersAreCancelled: boolean;
  namedReferences?: NamedReference[];
  debtCollectionConfig: DebtCollectionConfig;
  debtCollectionCancelled: boolean;
  sentToDebtCollection: boolean;
  allowSendingForDebtCollection: boolean;

  roundOff?: number;

  // Values calculated internally using rounding diffs to get accurate data
  totalVatAmount?: number;
  totalVatAmountsGroupedByVatPercentage?: VatEntry[];

  // This excludes both VAT and roundOff
  totalAmountExcludingVat?: number;

  history: InternalInvoiceHistoryEntryResponse[];
};

export type InvoiceState = {
  invoiceEntity?: UpdateInvoiceData | null;
  isFetchingUpdate: boolean;
  successUpdate?: boolean;
  errorUpdate?: any;
} & FetchingState;

export type UpdateInvoiceState = {
  data: InvoiceState;
};

const initialState: UpdateInvoiceState = { data: { isFetching: false, isFetchingUpdate: false } };

const updateInvoiceModalSlice = createSlice({
  name: 'invoices/actions/UpdateInvoiceModal',
  initialState,
  reducers: {
    reset: _ => initialState,
    patchInvoice: (state, { payload: invoicePatch }: PayloadAction<Partial<UpdateInvoiceData>>) => {
      if (state.data.invoiceEntity != null) {
        state.data.invoiceEntity = {
          ...state.data.invoiceEntity,
          ...invoicePatch,
          invoicePeriod: {
            ...state.data.invoiceEntity.invoicePeriod,
            ...invoicePatch.invoicePeriod,
          },
          invoiceCustomer: {
            ...state.data.invoiceEntity.invoiceCustomer,
            ...invoicePatch.invoiceCustomer,
            address: {
              ...state.data.invoiceEntity.invoiceCustomer.address,
              ...invoicePatch.invoiceCustomer?.address,
            },
          },
        };
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(updateInvoice.pending, (state, { meta: { arg } }) => {
      state.data.isFetchingUpdate = true;
      state.data.success = undefined;
      state.data.errorUpdate = null;
    });
    builder.addCase(updateInvoice.fulfilled, (state, { meta: { arg } }) => {
      state.data.isFetchingUpdate = false;
      state.data.successUpdate = true;
      state.data.errorUpdate = null;
    });
    builder.addCase(updateInvoice.rejected, (state, { meta: { arg }, error }) => {
      state.data.isFetchingUpdate = false;
      state.data.successUpdate = false;
      state.data.errorUpdate = error;
    });

    builder.addCase(fetchInvoiceByInvoiceNumber.pending, (state, { meta: { arg } }) => {
      state.data.isFetching = true;
      state.data.success = undefined;
      state.data.error = null;
    });
    builder.addCase(fetchInvoiceByInvoiceNumber.fulfilled, (state, { meta: { arg }, payload: invoice }) => {
      state.data.isFetching = false;
      state.data.success = true;
      state.data.error = null;
      state.data.invoiceEntity = invoice as UpdateInvoiceData;
    });
    builder.addCase(fetchInvoiceByInvoiceNumber.rejected, (state, { meta: { arg }, error }) => {
      state.data.isFetching = false;
      state.data.success = false;
      state.data.error = error;
    });
  },
});

export const {
  actions: { reset, patchInvoice },
  reducer: updateInvoiceModalReducer,
} = updateInvoiceModalSlice;
